import React, {useState, useEffect, useRef} from 'react';
import { useWebSocket } from '../../connections/WebSocketContext.js';
import './MeetingInsights.css';
import CoachingPoints from './CoachingPoints';

  const WRAP_UP_SECTION = 'wrap-up';
  const FOLLOW_UP_SECTION = 'follow-up';

const MeetingInsights = ({ nextSteps, updatedObjectives, conclusionData, meetingData, rawTranscript, onConclusionGenerated, questionsAnswers, actionItems, emphasisPoints }) => {
  const [activeTab, setActiveTab] = useState('coaching');
  const [expandedSections, setExpandedSections] = useState(new Set());
  const [isGeneratingConclusion, setIsGeneratingConclusion] = useState(false);

  // Reset generating state when conclusion data is received
  useEffect(() => {
    const actionItemsArray = actionItems?.actionItems || [];
    const emphasisPointsArray = emphasisPoints?.emphasisPoints || [];
    
    if (isGeneratingConclusion && (actionItemsArray.length > 0 || emphasisPointsArray.length > 0)) {
      console.log('📝 Conclusion data received, stopping generating state');
      setIsGeneratingConclusion(false);
    }
  }, [actionItems, emphasisPoints, isGeneratingConclusion]);
  const [scrollId, setScrollId] = useState(Date.now());
  const scrollRef = useRef();

  // Get WebSocket functions
  const { sendMessage } = useWebSocket();

  useEffect(() => {
    scrollRef.current?.scrollIntoView({ block: 'end' });
    setScrollId(Date.now());
  }, [nextSteps]);

  const toggleSection = (sectionName) => {
    const newExpanded = new Set();
    if (expandedSections.has(sectionName)) {
      newExpanded.delete(sectionName);
    } else {
      newExpanded.add(sectionName);
    }
    setExpandedSections(newExpanded);
  };

  const handleGenerateConclusion = async () => {
    console.log('🎯 Generating conclusion...');
    setIsGeneratingConclusion(true);

    try {
      // Check if WebSocket is connected before sending
      if (!sendMessage) {
        alert('WebSocket is not connected. Please connect first.');
        setIsGeneratingConclusion(false);
        return;
      }

      // Get instance ID from meeting data
      const instanceId = meetingData?.instanceId || 
                        meetingData?._id || 
                        meetingData?.entity?.id || 
                        meetingData?.id || 
                        meetingData?.meetingId;

      if (!instanceId) {
        alert('No instance ID found. Cannot generate conclusion.');
        setIsGeneratingConclusion(false);
        return;
      }

      // Send generate-conclusion message to WebSocket with instance_id
      sendMessage('generate-conclusion', { 
        value: true,
        instance_id: instanceId
      });
      
      console.log('📤 Sent generate-conclusion message to WebSocket with instance_id:', instanceId);
      
      // The conclusion data will be received via WebSocket and handled by the parent component
      // The button will be hidden when conclusion data is received
      // DO NOT set isGeneratingConclusion to false here - let it stay true until data is received
      
    } catch (error) {
      console.error('Error sending generate-conclusion message:', error);
      alert('Error generating conclusion messaging. Please try again.');
      setIsGeneratingConclusion(false);
    }
  };
  
  const renderCoachingTab = () => {
    return (
      <div className="insights-section">
        <CoachingPoints suggestions={nextSteps} />
      </div>
    );
  };

  const questions = questionsAnswers?.questionsAnalysis?.questionsAndConcerns || [];

  const renderQandATab = () => (
    <div className="insights-section">
      <div className="coaching-pointers-list">
        <div ref={scrollRef}>
          {questions.length ? (
            questions.map((item, index) => {
              // Handle both string and object formats for Q&A items
              let questionContent = '';
              let questionAnswer = '';
              
              if (typeof item === 'string') {
                questionContent = item;
                questionAnswer = '';
              } else if (item && typeof item === 'object') {
                questionContent = item.content || item.question || item.text || JSON.stringify(item);
                questionAnswer = item.answer || item.response || '';
              } else {
                questionContent = String(item);
                questionAnswer = '';
              }
              
              return (
                <div key={index} className="qa-pointer">
                  <i className="ti ti-question-mark pointer-icon"/>
                  <div className="qa-content">
                    <div className="title">{questionContent}</div>
                    <div className="text">{questionAnswer}</div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="empty-coaching-state">
              <div className="empty-state-icon">
                <i className="ti ti-question-mark"></i>
              </div>
              <p className="empty-state-text">
                Q&A will automatically appear here as the meeting progresses.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );



  const wrapUpPoints = emphasisPoints?.emphasisPoints || [];
  const followUpActions = actionItems?.actionItems || [];
  const conclusionPoints = [...wrapUpPoints, ...followUpActions];

  const renderConclusionTab = () => (
    <div className="insights-section conclusion-section">
      {!conclusionPoints.length ? (
        <div className="conclusion-empty-state">
          <div className="empty-state-icon">
            <i className="ti ti-sparkles"></i>
          </div>
          <p className="empty-state-text">
            {meetingData?.isCompleted 
              ? "No conclusion was generated for this meeting."
              : "Generate a comprehensive conclusion with wrap-up points and follow-up actions."
            }
          </p>
        </div>
      ) : (
        <>
          <div
            className="section-container"
            onClick={() => toggleSection(WRAP_UP_SECTION)}>
            <div className="section-item">
              <span className="section-title">Wrap-up Points</span>
              <span
                className="completion-percentage blue"
              >
                {wrapUpPoints.length}
              </span>
              <div className="objective-toggle">
                {expandedSections.has(WRAP_UP_SECTION) ? <i className="ti ti-chevron-down"/> : <i className="ti ti-chevron-right"/>}
              </div>
            </div>
            {expandedSections.has(WRAP_UP_SECTION) && (
              <div className="wrap-up-points-list">
                {wrapUpPoints.map((point, index) => {
                  // Handle both string and object formats
                  let pointText = '';
                  if (typeof point === 'string') {
                    pointText = point;
                  } else if (point && typeof point === 'object') {
                    // Extract the point text from the object
                    pointText = point.text || point.content || point.suggestion || JSON.stringify(point);
                  } else {
                    pointText = String(point);
                  }
                  
                  return (
                    <div key={index} className="wrap-up-point">
                      <i className="ti ti-circle"/>
                      <div className="point-text">{pointText}</div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div
            className="section-container"
            onClick={() => toggleSection(FOLLOW_UP_SECTION)}>
            <div className="section-item">
              <span className="section-title">Follow-up and Action Items</span>
              <span
                className="completion-percentage blue"
              >
                {followUpActions.length}
              </span>
              <div className="objective-toggle">
                {expandedSections.has(FOLLOW_UP_SECTION) ? <i className="ti ti-chevron-down"/> : <i className="ti ti-chevron-right"/>}
              </div>
            </div>
            {expandedSections.has(FOLLOW_UP_SECTION) && (
              <div className="follow-up-actions-list">
                {followUpActions.map((action, index) => {
                  // Handle both string and object formats
                  let actionText = '';
                  if (typeof action === 'string') {
                    actionText = action;
                  } else if (action && typeof action === 'object') {
                    // Extract the action text from the object
                    actionText = action.text || action.content || action.suggestion || JSON.stringify(action);
                  } else {
                    actionText = String(action);
                  }
                  
                  return (
                    <div key={index} className="follow-up-action">
                      <i className="ti ti-circle"/>
                      <div className="action-text">{actionText}</div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}
      
      {/* Conclusion Button - only show if no conclusion data, not currently generating, not in viewer mode, and not a completed meeting */}
      {!conclusionPoints.length && !isGeneratingConclusion && meetingData?.consumerType !== 'viewer' && !meetingData?.isCompleted && (
        <div className="conclusion-button-container">
          <button 
            onClick={handleGenerateConclusion}
            disabled={!meetingData || !rawTranscript}
            className="conclusion-button"
          >
            <i className="ti ti-sparkles"></i>
            Generate Conclusion
          </button>
        </div>
      )}

      {/* Loading state button */}
      {isGeneratingConclusion && (
        <div className="conclusion-button-container">
          <button 
            disabled={true}
            className="conclusion-button generating"
          >
            <i className="ti ti-loader-2"></i>
            Generating...
          </button>
        </div>
      )}
    </div>
  );

  return (
    <div className="meeting-insights-container">
      {/* Tab Navigation */}
      <div className="insights-tabs">
        <h3>Coaching</h3>
        <button 
          className={`insight-tab ${activeTab === 'coaching' ? 'active' : ''}`}
          onClick={() => setActiveTab('coaching')}
        >
          Meeting
        </button>
        <button
          className={`insight-tab ${activeTab === 'q&a' ? 'active' : ''}`}
          onClick={() => setActiveTab('q&a')}
        >
          Q&A
        </button>

        <button 
          className={`insight-tab ${activeTab === 'conclusion' ? 'active' : ''}`}
          onClick={() => setActiveTab('conclusion')}
        >
          Conclusion
        </button>
      </div>

      {/* Tab Content */}
      <div className="insights-content">
        {activeTab === 'coaching' && renderCoachingTab()}
        {activeTab === 'q&a' && renderQandATab()}

        {activeTab === 'conclusion' && renderConclusionTab()}
      </div>
    </div>
  );
};

export default MeetingInsights; 
